import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
  FiCheck, FiChevronLeft, FiUser, FiCreditCard, FiFileText,
  FiUpload, FiX, FiAlertCircle, FiInfo,
  FiArrowRight, FiMail, FiPhone, FiBriefcase, FiLock,
} from 'react-icons/fi';
import { RiBuilding2Line, RiPenNibLine } from 'react-icons/ri';
import { HiOutlineDocumentText } from 'react-icons/hi';
import ifaService from '../../services/ifaService';
import { processSupportingDocumentForUpload } from '../../utils/cmrFileProcessing';
import {
  applyBankManualUploadState,
  buildBankManualUploadState,
} from '../../utils/bankVerifyManualUpload';
import BankManualUploadErrorAlert from '../../components/bank/BankManualUploadErrorAlert';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';

// ─── Constants ───────────────────────────────────────────────────────────────

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const WIZARD_STEPS = [
  { key: 'STEP_0_EMAIL', label: 'Partner email', sublabel: 'Create or resume', icon: FiMail },
  { key: 'STEP_1_PAN', label: 'PAN Verification', sublabel: 'Verify PAN', icon: FiCreditCard },
  { key: 'STEP_2_PERSONAL', label: 'Personal Details', sublabel: 'DOB & mobile', icon: FiUser },
  { key: 'STEP_3_BANK', label: 'Bank Verification', sublabel: 'Account details', icon: RiBuilding2Line },
  { key: 'STEP_4_DOCUMENTS', label: 'Document Upload', sublabel: 'Optional — send invite', icon: FiFileText },
  { key: 'STEP_5_AGREEMENT', label: 'Agreement E-Sign', sublabel: 'Partner portal (DigiSign)', icon: RiPenNibLine },
  { key: 'STEP_6_QUESTIONNAIRE', label: 'Business Profile', sublabel: 'Partner questionnaire', icon: FiBriefcase },
  { key: 'STEP_COMPLETED', label: 'Onboarding complete', sublabel: 'Ready for transactions', icon: FiCheck },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getStepIndex(stepKey) {
  return WIZARD_STEPS.findIndex((s) => s.key === stepKey);
}

/** Map API current_step to wizard sidebar key */
function mapApiStepToWizard(apiStep) {
  if (!apiStep) return 'STEP_1_PAN';
  if (apiStep === 'COMPLETED') return 'STEP_COMPLETED';
  if (apiStep === 'STEP_2_DETAILS' || apiStep === 'STEP_2_PERSONAL') return 'STEP_2_PERSONAL';
  return apiStep;
}

/** Map API completed_steps entries to wizard keys (keep STEP_5 / STEP_6 distinct) */
function mapCompletedStepKey(key) {
  if (key === 'STEP_2_DETAILS' || key === 'STEP_2_PERSONAL') return 'STEP_2_PERSONAL';
  if (key === 'COMPLETED') return 'STEP_COMPLETED';
  return key;
}

function InputField({ label, error, className = '', children }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-sm font-medium text-gray-700">{label}</label>}
      {children}
      {error && <p className="text-xs text-red-500 flex items-center gap-1"><FiAlertCircle size={12} />{error}</p>}
    </div>
  );
}

function StyledInput({ value, onChange, placeholder, disabled, className = '', type = 'text', maxLength, autoFocus }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      autoFocus={autoFocus}
      className={`w-full px-3.5 py-2.5 rounded-lg border text-sm transition-all outline-none
        ${disabled
          ? 'bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed'
          : 'bg-white text-gray-900 border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'
        } ${className}`}
    />
  );
}

function GreenButton({ onClick, disabled, loading, children, className = '', type = 'button' }) {
  const inactive = disabled || loading;
  const stateClass = loading
    ? 'bg-green-600 text-white cursor-wait shadow-sm'
    : disabled
      ? 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none'
      : 'bg-green-600 text-white hover:bg-green-700 active:scale-[0.99] shadow-sm hover:shadow-md';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={inactive}
      className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all ${stateClass} ${className}`}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          Processing...
        </span>
      ) : children}
    </button>
  );
}

// ─── Step 0: Email Check ──────────────────────────────────────────────────────

function EmailStep({ onSuccess }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dualRoleNotice, setDualRoleNotice] = useState('');
  const [pendingResume, setPendingResume] = useState(null);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async () => {
    if (!isValidEmail) { setError('Please enter a valid email address.'); return; }

    if (pendingResume) {
      onSuccess(pendingResume.id, pendingResume.email, true);
      return;
    }

    setError('');
    setDualRoleNotice('');
    setLoading(true);
    try {
      const res = await ifaService.checkEmail(email);
      if (res.data?.exists) {
        if (res.data.is_dual_role) {
          setDualRoleNotice(
            res.data.message ||
              'This email belongs to an existing investor. Partner access will be added to the same account.'
          );
          setPendingResume({ id: res.data.id, email });
          toast.success(`Adding partner access for ${res.data.name || email}`);
          return;
        }
        toast.success(`Resuming onboarding for ${res.data.name || email}`);
        onSuccess(res.data.id, email, true);
      } else {
        // New partner record – create
        const createRes = await ifaService.createIFA(email);
        toast.success('Partner account created successfully!');
        onSuccess(createRes.data.user.id, email, false);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Something went wrong. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center shrink-0">
          <FiMail size={22} className="text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Partner email address</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            We&apos;ll check if this email already exists. If not, we create the partner record—no OTP on this screen.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        <InputField label="Email address" error={error}>
          <StyledInput
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError('');
              setDualRoleNotice('');
              setPendingResume(null);
            }}
            placeholder="partner@example.com"
            autoFocus
          />
        </InputField>

        {dualRoleNotice ? (
          <div
            role="status"
            className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
          >
            <FiInfo size={18} className="shrink-0 mt-0.5" />
            <span>{dualRoleNotice}</span>
          </div>
        ) : null}

        <GreenButton
          onClick={handleSubmit}
          disabled={!isValidEmail}
          loading={loading}
        >
          {pendingResume ? 'Continue with same account' : 'Continue'}
          <FiArrowRight className="inline ml-1" />
        </GreenButton>
      </div>
    </div>
  );
}

// ─── Step 1: PAN (PATCH /pan — body { pan } only; backend sets name) ───────

function PANStep({ ifaId, onSuccess, initialPan = '' }) {
  const [pan, setPan] = useState(initialPan);
  const [lookupName, setLookupName] = useState('');
  const [panLookupLoading, setPanLookupLoading] = useState(false);
  const [panVerified, setPanVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const panValue = pan.toUpperCase();
  const isPanValid = PAN_REGEX.test(panValue);

  useEffect(() => {
    if (!isPanValid) { setLookupName(''); setPanVerified(false); return; }
    let cancelled = false;
    const lookup = async () => {
      setPanLookupLoading(true);
      try {
        const res = await ifaService.lookupPAN(panValue);
        if (!cancelled) {
          setLookupName(res.data?.name || '');
          setPanVerified(true);
        }
      } catch {
        if (!cancelled) {
          setLookupName('');
          setPanVerified(false);
        }
      } finally {
        if (!cancelled) setPanLookupLoading(false);
      }
    };
    lookup();
    return () => { cancelled = true; };
  }, [panValue, isPanValid]);

  const handleSubmit = async () => {
    if (!isPanValid) {
      setErrors({ pan: 'Enter a valid PAN (e.g. ABCDE1234F)' });
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await ifaService.updatePan(ifaId, { pan: panValue });
      toast.success('PAN verified and saved!');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save PAN.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">PAN verification</h3>
        <p className="text-sm text-gray-500 mt-0.5">PAN is verified with the registrar; name is prefilled when available.</p>
      </div>

      <div className="flex flex-col gap-5">
        <InputField label="PAN number" error={errors.pan}>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <FiCreditCard size={16} />
            </div>
            <input
              value={panValue}
              onChange={(e) => { setPan(e.target.value.toUpperCase()); setErrors((p) => ({ ...p, pan: '' })); }}
              placeholder="ABCDE1234F"
              maxLength={10}
              className={`w-full pl-9 pr-10 py-2.5 rounded-lg border text-sm transition-all outline-none uppercase
                ${errors.pan ? 'border-red-400 focus:ring-red-500/10' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {panLookupLoading && (
                <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
              )}
              {!panLookupLoading && panVerified && <FiCheck size={16} className="text-green-500" />}
            </div>
          </div>
          {panVerified && lookupName && (
            <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
              <FiCheck size={12} /> Name on PAN: <span className="font-medium">{lookupName}</span>
            </p>
          )}
        </InputField>

        {lookupName && (
          <InputField label="Name (as per PAN)">
            <StyledInput value={lookupName} disabled placeholder="From PAN lookup" />
          </InputField>
        )}

        <GreenButton
          onClick={handleSubmit}
          disabled={!isPanValid}
          loading={loading}
        >
          Continue
        </GreenButton>
      </div>
    </div>
  );
}

// ─── Step 2: Personal (PATCH /personal — { dob, mobile }) ────────────────────

function PersonalStep({ ifaId, initialMobile, initialDob = '', onSuccess }) {
  const [dob, setDob] = useState(initialDob || '');
  const [mobile, setMobile] = useState((initialMobile && initialMobile !== '0000000000' ? initialMobile : '') || '');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!dob) e.dob = 'Date of birth is required';
    if (!/^\d{10}$/.test(mobile)) e.mobile = 'Enter a valid 10-digit mobile number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await ifaService.updatePersonal(ifaId, { dob, mobile });
      toast.success('Personal details saved!');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save personal details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Personal details</h3>
        <p className="text-sm text-gray-500 mt-0.5">Enter date of birth and mobile number for the partner.</p>
      </div>

      <div className="flex flex-col gap-5">
        <InputField label="Date of birth" error={errors.dob}>
          <DatePicker
            size="large"
            className="w-full min-w-0"
            value={dob ? dayjs(dob, 'YYYY-MM-DD') : null}
            onChange={(d) => {
              setDob(d ? d.format('YYYY-MM-DD') : '');
              setErrors((p) => ({ ...p, dob: '' }));
            }}
            format="DD/MM/YYYY"
            placeholder="Select date of birth"
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            status={errors.dob ? 'error' : undefined}
          />
        </InputField>

        <InputField label="Mobile number" error={errors.mobile}>
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><FiPhone size={16} /></div>
            <input
              value={mobile}
              onChange={(e) => { setMobile(e.target.value.replace(/\D/g, '')); setErrors((p) => ({ ...p, mobile: '' })); }}
              placeholder="10-digit mobile number"
              maxLength={10}
              className={`w-full pl-9 py-2.5 rounded-lg border text-sm transition-all outline-none
                ${errors.mobile ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
            />
          </div>
        </InputField>

        <GreenButton onClick={handleSubmit} disabled={!dob || mobile.length !== 10} loading={loading}>
          Continue
        </GreenButton>
      </div>
    </div>
  );
}

// ─── Step 3: Bank Details ─────────────────────────────────────────────────────

function isPreviewImageFile(file) {
  if (!file) return false;
  const mime = String(file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  const name = String(file.name || '').trim().toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp|heic|heif)$/i.test(name);
}

function UploadedFilePreview({ file }) {
  const previewUrl = useMemo(() => {
    if (!file || !isPreviewImageFile(file)) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      {previewUrl ? (
        <div className="w-full rounded-lg overflow-hidden border border-green-200/80 bg-gray-50 max-h-52 flex items-center justify-center">
          <img
            src={previewUrl}
            alt={`Preview: ${file.name}`}
            className="max-h-52 w-full object-contain"
          />
        </div>
      ) : (
        <div
          className="w-20 h-20 rounded-xl bg-green-100 border border-green-200/70 flex items-center justify-center"
          aria-hidden
        >
          <HiOutlineDocumentText className="w-10 h-10 text-green-700" />
        </div>
      )}
      <div className="text-center min-w-0 w-full">
        <p className="text-sm text-gray-700 font-medium break-all">{file.name}</p>
        <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
      </div>
    </div>
  );
}

function BankStep({ ifaId, mobile, customerName, onSuccess, prefillBanks = [], selectedBankId }) {
  const hasPrefill = prefillBanks && prefillBanks.length > 0;
  const [bankMode, setBankMode] = useState(hasPrefill ? 'existing' : 'mobile'); // 'existing' | 'mobile' | 'manual'
  const [selectedPrefillBankId, setSelectedPrefillBankId] = useState(selectedBankId || (hasPrefill ? prefillBanks[0].bank_account_id : ''));
  const [mobileBankData, setMobileBankData] = useState(null);
  const [mobileBankLoading, setMobileBankLoading] = useState(false);
  const [mobileBankError, setMobileBankError] = useState('');

  const [accountNumber, setAccountNumber] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [bankName, setBankName] = useState('');
  const [branchName, setBranchName] = useState('');
  const [ifscLookupLoading, setIfscLookupLoading] = useState(false);
  const [ifscVerified, setIfscVerified] = useState(false);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [mismatchData, setMismatchData] = useState(null);
  const [chequeFile, setChequeFile] = useState(null);
  const [otherDocFile, setOtherDocFile] = useState(null);
  const [chequeFileProcessing, setChequeFileProcessing] = useState(false);
  const [otherDocFileProcessing, setOtherDocFileProcessing] = useState(false);
  const chequeFileInputRef = useRef(null);
  const otherDocFileInputRef = useRef(null);

  // Fetch bank from mobile on mount (for 'mobile' mode)
  useEffect(() => {
    if (!mobile) return;
    setMobileBankLoading(true);
    setMobileBankError('');
    ifaService.getBankFromMobile(mobile, customerName || null, ifaId)
      .then((res) => {
        setMobileBankData(res.data?.suggested_bank || null);
      })
      .catch(() => {
        setMobileBankError('Could not fetch bank details linked to mobile number.');
      })
      .finally(() => setMobileBankLoading(false));
  }, [mobile, customerName, ifaId]);

  // IFSC lookup on valid regex
  const ifscValue = ifsc.toUpperCase();
  useEffect(() => {
    if (bankMode !== 'manual') return;
    if (!IFSC_REGEX.test(ifscValue)) { setBankName(''); setBranchName(''); setIfscVerified(false); return; }
    let cancelled = false;
    const lookup = async () => {
      setIfscLookupLoading(true);
      try {
        const res = await ifaService.lookupIFSC(ifscValue);
        if (!cancelled) {
          setBankName(res.data?.bank_name || '');
          setBranchName(res.data?.branch_name || '');
          setIfscVerified(true);
        }
      } catch {
        if (!cancelled) { setBankName(''); setBranchName(''); setIfscVerified(false); }
      } finally {
        if (!cancelled) setIfscLookupLoading(false);
      }
    };
    lookup();
    return () => { cancelled = true; };
  }, [ifscValue, bankMode]);

  const getSubmitData = () => {
    if (bankMode === 'existing') {
      return { bank_account_id: selectedPrefillBankId };
    }
    if (bankMode === 'mobile' && mobileBankData) {
      return {
        account_number: mobileBankData.account_number,
        ifsc_code: mobileBankData.ifsc_code,
        bank_name: mobileBankData.bank_name,
      };
    }
    return { account_number: accountNumber, ifsc_code: ifscValue, bank_name: bankName };
  };

  const validate = () => {
    const e = {};
    if (bankMode === 'existing') {
      if (!selectedPrefillBankId) e.general = 'Please select an existing bank account.';
    } else if (bankMode === 'manual') {
      if (!accountNumber.trim()) e.accountNumber = 'Account number is required';
      if (!IFSC_REGEX.test(ifscValue)) e.ifsc = 'Enter a valid IFSC code';
      if (!bankName.trim()) e.bankName = 'Bank name is required (fetched via IFSC lookup)';
    } else if (!mobileBankData) {
      e.general = 'Mobile-linked bank data not available. Please use manual entry.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const getAttemptedBankFields = () => {
    if (bankMode === 'existing' && selectedPrefillBankId) {
      const bank = prefillBanks.find((b) => b.bank_account_id === selectedPrefillBankId);
      if (bank) {
        return {
          accountNumber: bank.account_number,
          ifsc: bank.ifsc_code || bank.ifsc,
          bankName: bank.bank_name,
        };
      }
    }
    if (bankMode === 'mobile' && mobileBankData) {
      return {
        accountNumber: mobileBankData.account_number,
        ifsc: mobileBankData.ifsc_code,
        bankName: mobileBankData.bank_name,
        branchName: mobileBankData.branch_name || '',
      };
    }
    return {
      accountNumber,
      ifsc: ifscValue,
      bankName,
      branchName,
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await ifaService.updateBankDetails(ifaId, getSubmitData());
      toast.success('Bank details saved successfully!');
      onSuccess();
    } catch (err) {
      const manualUpload = buildBankManualUploadState(err, getAttemptedBankFields());
      applyBankManualUploadState(manualUpload, {
        setAccountNumber,
        setIfsc,
        setBankName,
        setBranchName,
        setBankMode,
        setMismatchData,
        setErrors,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMismatchSubmit = async () => {
    if (!chequeFile) {
      setErrors({ cheque: 'Cancelled cheque / supporting document is required' });
      return;
    }
    if (chequeFileProcessing || otherDocFileProcessing) return;
    if (!accountNumber.trim()) {
      setErrors((p) => ({ ...p, accountNumber: 'Account number is required' }));
      return;
    }
    if (!IFSC_REGEX.test(ifscValue)) {
      setErrors((p) => ({ ...p, ifsc: 'Enter a valid IFSC code' }));
      return;
    }
    if (!bankName.trim()) {
      setErrors((p) => ({ ...p, bankName: 'Bank name is required (use IFSC lookup)' }));
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('account_number', accountNumber.trim());
      fd.append('ifsc_code', ifscValue);
      fd.append('bank_name', bankName.trim());
      fd.append('branch_name', branchName || '');
      fd.append('cheque', chequeFile);
      if (otherDocFile) fd.append('supporting_document', otherDocFile);
      await ifaService.uploadBankMismatchCheque(ifaId, fd);
      toast.success('Documents submitted for review!');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to upload documents.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBackFromMismatch = () => {
    setMismatchData(null);
    setChequeFile(null);
    setOtherDocFile(null);
    setChequeFileProcessing(false);
    setOtherDocFileProcessing(false);
    setErrors({});
  };

  const applyChequeFile = async (file) => {
    if (!file) return;
    setChequeFileProcessing(true);
    try {
      const result = await processSupportingDocumentForUpload(file);
      if (!result.ok) {
        toast.error(result.error);
        setErrors((p) => ({ ...p, cheque: result.error }));
        return;
      }
      setChequeFile(result.file);
      setErrors((p) => ({ ...p, cheque: '' }));
      if (result.wasCompressed) {
        toast.success('Image compressed to under 2 MB.');
      }
    } finally {
      setChequeFileProcessing(false);
    }
  };

  const applyOtherDocFile = async (file) => {
    if (!file) return;
    setOtherDocFileProcessing(true);
    try {
      const result = await processSupportingDocumentForUpload(file);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setOtherDocFile(result.file);
      if (result.wasCompressed) {
        toast.success('Image compressed to under 2 MB.');
      }
    } finally {
      setOtherDocFileProcessing(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Bank Account</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          {mismatchData
            ? 'Bank verification could not be completed automatically. Upload documents for manual review.'
            : 'Use the mobile-linked account or enter details manually.'}
        </p>
      </div>

      {mismatchData ? (
        <div className="flex flex-col gap-5">
          <BankManualUploadErrorAlert manualUploadData={mismatchData} />

          <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
            <InputField label="Account number" error={errors.accountNumber}>
              <StyledInput
                value={accountNumber}
                onChange={(e) => {
                  setAccountNumber(e.target.value);
                  setErrors((p) => ({ ...p, accountNumber: '' }));
                }}
                placeholder="Enter account number"
              />
            </InputField>

            <InputField label="IFSC" error={errors.ifsc}>
              <div className="relative">
                <input
                  value={ifscValue}
                  onChange={(e) => {
                    setIfsc(e.target.value.toUpperCase());
                    setErrors((p) => ({ ...p, ifsc: '' }));
                  }}
                  placeholder="e.g. HDFC0001234"
                  maxLength={11}
                  className={`w-full px-3.5 pr-10 py-2.5 rounded-lg border text-sm transition-all outline-none uppercase
                    ${errors.ifsc ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {ifscLookupLoading && (
                    <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  )}
                  {!ifscLookupLoading && ifscVerified && <FiCheck size={16} className="text-green-500" />}
                </div>
              </div>
            </InputField>

            <InputField label="Bank name" error={errors.bankName}>
              <StyledInput
                value={bankName}
                placeholder={ifscLookupLoading ? 'Fetching from IFSC...' : 'Auto-filled from IFSC lookup'}
                disabled
              />
            </InputField>

            {branchName && (
              <div className="bg-green-50 border border-green-200 rounded-lg px-3.5 py-2.5 text-sm text-green-700">
                Branch: <span className="font-medium">{branchName}</span>
              </div>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Cancelled cheque / supporting document <span className="text-red-500">*</span>
            </p>
            {errors.cheque && (
              <p className="text-xs text-red-500 flex items-center gap-1 mb-1">
                <FiAlertCircle size={12} /> {errors.cheque}
              </p>
            )}
            <div
              role="button"
              tabIndex={0}
              onClick={() => !loading && !chequeFileProcessing && chequeFileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && !chequeFileProcessing && chequeFileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center gap-2 transition-all
                ${chequeFileProcessing ? 'border-gray-200 bg-gray-50 cursor-wait opacity-80' : 'cursor-pointer'}
                ${!chequeFileProcessing && (chequeFile ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30')}`}
            >
              {chequeFileProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p className="text-sm text-gray-500">Processing file...</p>
                </>
              ) : chequeFile ? (
                <UploadedFilePreview file={chequeFile} />
              ) : (
                <>
                  <FiUpload size={20} className="text-gray-400" />
                  <p className="text-sm text-gray-500">Click to select file</p>
                  <p className="text-xs text-gray-400">PDF max 2 MB · Images up to 5 MB (auto-compressed to 2 MB)</p>
                </>
              )}
            </div>
            <input
              ref={chequeFileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={chequeFileProcessing}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) applyChequeFile(f);
              }}
            />
            {chequeFile && !chequeFileProcessing && (
              <button
                type="button"
                onClick={() => setChequeFile(null)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FiX size={12} /> Remove file
              </button>
            )}
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Other supporting documents (optional)</p>
            <div
              role="button"
              tabIndex={0}
              onClick={() => !loading && !otherDocFileProcessing && otherDocFileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && !otherDocFileProcessing && otherDocFileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center gap-2 transition-all
                ${otherDocFileProcessing ? 'border-gray-200 bg-gray-50 cursor-wait opacity-80' : 'cursor-pointer'}
                ${!otherDocFileProcessing && (otherDocFile ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30')}`}
            >
              {otherDocFileProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" aria-hidden>
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  <p className="text-sm text-gray-500">Processing file...</p>
                </>
              ) : otherDocFile ? (
                <UploadedFilePreview file={otherDocFile} />
              ) : (
                <>
                  <FiUpload size={20} className="text-gray-400" />
                  <p className="text-sm text-gray-500">Click to select file</p>
                  <p className="text-xs text-gray-400">PDF max 2 MB · Images up to 5 MB (auto-compressed to 2 MB)</p>
                </>
              )}
            </div>
            <input
              ref={otherDocFileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              disabled={otherDocFileProcessing}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) applyOtherDocFile(f);
              }}
            />
            {otherDocFile && !otherDocFileProcessing && (
              <button
                type="button"
                onClick={() => setOtherDocFile(null)}
                className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <FiX size={12} /> Remove file
              </button>
            )}
          </div>

          <GreenButton
            onClick={handleMismatchSubmit}
            loading={loading}
            disabled={
              chequeFileProcessing ||
              otherDocFileProcessing ||
              !accountNumber.trim() ||
              !ifscVerified ||
              !chequeFile
            }
          >
            Submit for Review
          </GreenButton>

          <button
            type="button"
            onClick={handleGoBackFromMismatch}
            className="text-sm text-gray-500 hover:text-gray-700 text-center"
          >
            Go back to bank verification
          </button>
        </div>
      ) : (
        <>
      {/* Bank mode selector */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-gray-700">Investor bank account</p>
          <RiBuilding2Line size={20} className="text-green-600" />
        </div>
        <div className="flex flex-col gap-2.5">
          {[
            hasPrefill && { value: 'existing', label: 'Use an existing verified bank account' },
            { value: 'mobile', label: 'Use primary account linked to partner mobile' },
            { value: 'manual', label: 'Enter account manually' },
          ].filter(Boolean).map((opt) => (
            <label key={opt.value} className="flex items-center gap-2.5 cursor-pointer group">
              <div
                onClick={() => setBankMode(opt.value)}
                className={`w-4.5 h-4.5 rounded-full border-2 flex items-center justify-center transition-all cursor-pointer
                  ${bankMode === opt.value ? 'border-green-600 bg-green-600' : 'border-gray-400 bg-white group-hover:border-green-400'}`}
                style={{ width: 18, height: 18 }}
              >
                {bankMode === opt.value && <div className="w-2 h-2 bg-white rounded-full" />}
              </div>
              <span
                onClick={() => setBankMode(opt.value)}
                className={`text-sm cursor-pointer ${bankMode === opt.value ? 'text-gray-900 font-medium' : 'text-gray-500'}`}
              >
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2 text-sm text-red-600">
          <FiAlertCircle size={16} className="shrink-0 mt-0.5" /> {errors.general}
        </div>
      )}

      {/* Existing bank selection */}
      {bankMode === 'existing' && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
          <InputField label="Select existing bank account">
            <select
              value={selectedPrefillBankId}
              onChange={(e) => setSelectedPrefillBankId(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 text-sm outline-none hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10 bg-white"
            >
              {prefillBanks.map((b) => (
                <option key={b.bank_account_id} value={b.bank_account_id}>
                  {b.bank_name} - {b.account_number} ({b.ifsc})
                </option>
              ))}
            </select>
          </InputField>
        </div>
      )}

      {/* Mobile-linked bank display */}
      {bankMode === 'mobile' && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
          {mobileBankLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
              Fetching bank details linked to mobile...
            </div>
          )}
          {mobileBankError && !mobileBankLoading && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex gap-2">
              <FiAlertCircle size={16} className="shrink-0 mt-0.5" /> {mobileBankError}
            </div>
          )}
          {mobileBankData && !mobileBankLoading && (
            <>
              <InputField label="Account number">
                <StyledInput value={mobileBankData.account_number} disabled />
              </InputField>
              <InputField label="IFSC">
                <StyledInput value={mobileBankData.ifsc_code} disabled />
              </InputField>
              <InputField label="Bank name">
                <StyledInput value={mobileBankData.bank_name} disabled />
              </InputField>
              <InputField label="Name as per bank">
                <StyledInput value={mobileBankData.name_as_per_bank} disabled />
              </InputField>
            </>
          )}
        </div>
      )}

      {/* Manual entry */}
      {bankMode === 'manual' && !mismatchData && (
        <div className="rounded-xl border border-gray-200 p-5 flex flex-col gap-4">
          <InputField label="Account number" error={errors.accountNumber}>
            <StyledInput
              value={accountNumber}
              onChange={(e) => { setAccountNumber(e.target.value); setErrors((p) => ({ ...p, accountNumber: '' })); }}
              placeholder="Enter account number"
            />
          </InputField>

          <InputField label="IFSC" error={errors.ifsc}>
            <div className="relative">
              <input
                value={ifscValue}
                onChange={(e) => { setIfsc(e.target.value.toUpperCase()); setErrors((p) => ({ ...p, ifsc: '' })); }}
                placeholder="e.g. HDFC0001234"
                maxLength={11}
                className={`w-full px-3.5 pr-10 py-2.5 rounded-lg border text-sm transition-all outline-none uppercase
                  ${errors.ifsc ? 'border-red-400' : 'border-gray-300 hover:border-green-400 focus:border-green-500 focus:ring-2 focus:ring-green-500/10'}`}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {ifscLookupLoading && (
                  <svg className="animate-spin h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                )}
                {!ifscLookupLoading && ifscVerified && <FiCheck size={16} className="text-green-500" />}
              </div>
            </div>
          </InputField>

          <InputField label="Bank name" error={errors.bankName}>
            <StyledInput
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder={ifscLookupLoading ? 'Fetching from IFSC...' : 'Auto-filled from IFSC lookup'}
              disabled={ifscLookupLoading}
            />
          </InputField>

          {branchName && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-3.5 py-2.5 text-sm text-green-700">
              Branch: <span className="font-medium">{branchName}</span>
            </div>
          )}
        </div>
      )}

      <GreenButton
        onClick={handleSubmit}
        loading={loading}
        disabled={
          (bankMode === 'mobile' && !mobileBankData && !mobileBankLoading) ||
          (bankMode === 'manual' && (!accountNumber || !ifscVerified)) ||
          (bankMode === 'existing' && !selectedPrefillBankId)
        }
      >
        Verify &amp; Continue
      </GreenButton>
        </>
      )}
    </div>
  );
}

// ─── Step 4: Document Upload ──────────────────────────────────────────────────

function isImageFile(file) {
  if (!file) return false;
  if (file.type?.startsWith('image/')) return true;
  return /\.(jpe?g|png)$/i.test(file.name);
}

function DocumentUploadCard({ doc, ifaId, onUploaded, onSkipOptional, initialUploaded = false }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [gstNumber, setGstNumber] = useState('');
  const [gstError, setGstError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(initialUploaded);

  const isGST = doc.type === 'GST_CERTIFICATE';

  useEffect(() => {
    if (!file || !isImageFile(file)) {
      setImagePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error('File size must be under 2 MB.'); return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (isGST && gstNumber && !GST_REGEX.test(gstNumber)) {
      setGstError('Enter a valid GST number');
      return;
    }
    if (isGST && file && !gstNumber) {
      setGstError('GST number is required when uploading GST certificate');
      return;
    }
    if (isGST && gstNumber && !file) {
      toast.error('Please upload the GST certificate document as well.');
      return;
    }
    if (!file) { toast.error('Please select a file to upload.'); return; }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document_type', doc.type);
      formData.append('document_number', isGST ? gstNumber : '');
      formData.append('file', file);
      await ifaService.uploadDocument(ifaId, formData);
      setUploaded(true);
      toast.success(`${doc.label} uploaded successfully!`);
      onUploaded(doc);
    } catch (err) {
      toast.error(err?.response?.data?.message || `Failed to upload ${doc.label}.`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`rounded-xl border p-5 transition-all ${uploaded ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${uploaded ? 'bg-green-100' : 'bg-gray-100'}`}>
            {uploaded
              ? <FiCheck size={16} className="text-green-600" />
              : <HiOutlineDocumentText size={18} className="text-gray-500" />
            }
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">{doc.label}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {doc.optional && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Optional</span>}
              {!doc.optional && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-medium">Required</span>}
              {doc.hard_copy && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">Hard copy</span>}
            </div>
          </div>
        </div>
        {uploaded && <span className="text-xs text-green-600 font-semibold bg-green-100 px-2.5 py-1 rounded-full flex items-center gap-1"><FiCheck size={11} />Uploaded</span>}
      </div>

      {!uploaded && (
        <div className="flex flex-col gap-3">
          {/* GST Number field */}
          {isGST && (
            <InputField label="GST Number" error={gstError}>
              <StyledInput
                value={gstNumber}
                onChange={(e) => { setGstNumber(e.target.value.toUpperCase()); setGstError(''); }}
                placeholder="22AAAAA0000A1Z5"
                maxLength={15}
              />
              <p className="text-xs text-gray-400 mt-0.5">Both GST number and certificate must be provided together or not at all.</p>
            </InputField>
          )}

          {/* File selector */}
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center gap-2 cursor-pointer transition-all
              ${file ? 'border-green-400 bg-green-50/50' : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30'}`}
          >
            {file ? (
              <div className="flex flex-col items-center gap-3 w-full">
                {imagePreviewUrl ? (
                  <div className="w-full rounded-lg overflow-hidden border border-green-200/80 bg-gray-50 max-h-52 flex items-center justify-center">
                    <img
                      src={imagePreviewUrl}
                      alt={`Preview: ${file.name}`}
                      className="max-h-52 w-full object-contain"
                    />
                  </div>
                ) : (
                  <div
                    className="w-20 h-20 rounded-xl bg-green-100 border border-green-200/70 flex items-center justify-center"
                    aria-hidden
                  >
                    <HiOutlineDocumentText className="w-10 h-10 text-green-700" />
                  </div>
                )}
                <div className="text-center min-w-0 w-full">
                  <p className="text-sm text-gray-700 font-medium break-all">{file.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
            ) : (
              <>
                <FiUpload size={20} className="text-gray-400" />
                <p className="text-sm text-gray-500">Click to select file</p>
                <p className="text-xs text-gray-400">PDF, JPG, PNG — max 2 MB</p>
              </>
            )}
          </div>
          <input ref={fileInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} />

          {file && (
            <div className="flex gap-2">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex-1 py-2 px-3 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <><svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg> Uploading...</>
                ) : (
                  <><FiUpload size={14} /> Upload</>
                )}
              </button>
              <button
                onClick={() => setFile(null)}
                disabled={uploading}
                className="py-2 px-3 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all disabled:opacity-50"
              >
                <FiX size={14} />
              </button>
            </div>
          )}

          {doc.optional && onSkipOptional && (
            <button
              type="button"
              onClick={onSkipOptional}
              disabled={uploading}
              className="w-full py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border border-dashed border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Skip this document
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function DocumentsStep({ ifaId, pendingDocs = [], uploadedDocs = [], onSendInviteComplete }) {
  const [newlyUploaded, setNewlyUploaded] = useState([]);
  const [sendInviteLoading, setSendInviteLoading] = useState(false);

  const handleAdvanceAfterUpload = (doc) => {
    setNewlyUploaded((prev) => [...prev, doc.type]);
  };

  const handleSkipOptionalDoc = (doc) => {
    setNewlyUploaded((prev) => [...prev, doc.type]);
  };

  const sendInviteAndFinalize = async () => {
    setSendInviteLoading(true);
    try {
      await ifaService.sendInvite(ifaId);
      toast.success('Invite sent successfully!');
      onSendInviteComplete();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send invite.');
    } finally {
      setSendInviteLoading(false);
    }
  };

  const requiredDocsCount = uploadedDocs.filter((d) => !d.optional).length + pendingDocs.filter((d) => !d.optional).length;
  const uploadedRequiredCount = uploadedDocs.filter((d) => !d.optional).length + 
        pendingDocs.filter((d) => !d.optional && newlyUploaded.includes(d.type)).length;
  
  const allRequiredUploaded = requiredDocsCount === 0 || uploadedRequiredCount >= requiredDocsCount;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-semibold text-gray-900">Document upload</h3>
        <p className="text-sm text-gray-500">
          Upload the required documents for this partner. You can send the invite once all required documents are uploaded.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {uploadedDocs.map((doc, idx) => (
          <DocumentUploadCard
            key={`uploaded-${doc.type}-${idx}`}
            doc={doc}
            ifaId={ifaId}
            initialUploaded={true}
          />
        ))}

        {pendingDocs.map((doc, idx) => {
          if (newlyUploaded.includes(doc.type) && doc.optional) {
              return null;
          }
          
          return (
            <DocumentUploadCard
              key={`pending-${doc.type}-${idx}`}
              doc={doc}
              ifaId={ifaId}
              onUploaded={() => handleAdvanceAfterUpload(doc)}
              onSkipOptional={doc.optional ? () => handleSkipOptionalDoc(doc) : undefined}
            />
          );
        })}
      </div>

      {allRequiredUploaded && (
        <div className="mt-4 border-t border-gray-100 pt-6">
          <div className="rounded-xl border border-green-100 bg-green-50/60 p-5 mb-5 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
              <FiCheck size={18} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Ready to send invite</p>
              <p className="text-xs text-gray-600 mt-0.5">All required documents have been provided. You can now invite the partner to their portal.</p>
            </div>
          </div>

          <button
            type="button"
            onClick={sendInviteAndFinalize}
            disabled={sendInviteLoading}
            className="w-full py-2.5 px-4 rounded-lg font-semibold text-sm bg-green-600 text-white hover:bg-green-700 transition-all shadow-sm hover:shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {sendInviteLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Sending invite…
              </>
            ) : (
              'Send invite to partner'
            )}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Fully onboarded: API current_step === COMPLETED ─────────────────────────

function IfaProfileCompletePanel({ ifaEmail, onViewList, onOnboardAnother, onRefresh, refreshLoading }) {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Partner profile complete</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          This partner has finished every onboarding step in the Partner portal, including agreement e-sign and the business questionnaire.
        </p>
      </div>

      <div className="rounded-2xl border border-green-100 bg-linear-to-br from-green-50/95 to-white p-8 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-green-600 flex items-center justify-center mb-4 shadow-md shadow-green-600/25">
            <FiCheck className="w-8 h-8 text-white" strokeWidth={2.5} />
          </div>
          <p className="text-base font-semibold text-gray-900">Ready for transactions</p>
          <p className="text-sm text-gray-600 mt-2 leading-relaxed max-w-md">
            Their profile is fully verified and active. They can operate according to your usual partner and transaction rules.
          </p>
          <ul className="w-full max-w-md mt-6 text-left text-sm text-gray-600 space-y-2.5">
            {[
              'Onboarding agreement signed (DigiSign)',
              'Business profile questionnaire submitted',
              'No further admin action required for onboarding',
            ].map((line) => (
              <li key={line} className="flex gap-2">
                <span className="text-green-600 shrink-0 mt-0.5" aria-hidden><FiCheck size={14} /></span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {ifaEmail && (
        <div className="rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-3 text-left">
          <p className="text-xs text-gray-500">Partner account</p>
          <p className="text-sm font-medium text-gray-900 mt-0.5 break-all">{ifaEmail}</p>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        <button
          type="button"
          onClick={onViewList}
          className="w-full py-2.5 px-4 rounded-lg font-semibold text-sm bg-green-600 text-white hover:bg-green-700 transition-all shadow-sm hover:shadow-md"
        >
          View Partner list
        </button>
        <button
          type="button"
          onClick={onOnboardAnother}
          className="w-full py-2.5 px-4 rounded-lg font-medium text-sm border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 transition-all"
        >
          Onboard another partner
        </button>
        <GreenButton onClick={onRefresh} loading={refreshLoading}>
          Refresh status
        </GreenButton>
      </div>
    </div>
  );
}

// ─── Steps 5 & 6: Partner completes in their portal (not admin) ───────────────────

function IfaPortalWaitingPanel({ stepKey, onRefresh, refreshLoading }) {
  const isAgreement = stepKey === 'STEP_5_AGREEMENT';
  const Icon = isAgreement ? RiPenNibLine : FiBriefcase;

  const title = isAgreement ? 'Agreement e-sign' : 'Business profile questionnaire';
  const lead = isAgreement
    ? 'This step is not completed yet. The partner must sign the onboarding agreement from the Partner portal.'
    : 'This step is not completed yet. The partner must answer the business profile questionnaire in the Partner portal.';

  const bullets = isAgreement
    ? [
        'Signing uses the DigiSign flow in the partner-facing app or portal (not this admin dashboard).',
        'After the partner finishes, their status will move to the next step automatically.',
      ]
    : [
        'The partner will see multiple questions about their business and profile.',
        'Answers are submitted only from the Partner portal.',
      ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 mt-0.5">Waiting on the partner — admin cannot complete this here.</p>
      </div>

      <div className="rounded-2xl border border-amber-100 bg-linear-to-br from-amber-50/90 to-white p-8 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
            <Icon className="w-8 h-8 text-amber-800" />
          </div>
          <p className="text-sm text-gray-700 leading-relaxed max-w-md">{lead}</p>
          <ul className="w-full max-w-md mt-6 text-left text-sm text-gray-600 space-y-2.5">
            {bullets.map((line) => (
              <li key={line} className="flex gap-2">
                <span className="text-green-600 shrink-0 mt-0.5" aria-hidden><FiCheck size={14} /></span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-sm text-blue-900">
        <span className="font-semibold">Tip:</span> Ask the partner to log in to their portal. Use refresh below after they finish to see the latest step.
      </div>

      <GreenButton onClick={onRefresh} loading={refreshLoading}>
        Refresh status
      </GreenButton>
    </div>
  );
}

// ─── Shared wizard chrome (sidebar + form column) ─────────────────────────────

function OnboardingWizardLayout({
  children,
  completedStepKeys,
  currentStepKey,
  maxReachedKey,
  ifaEmail,
  percentComplete,
  sidebarHint,
  onBackToList,
  onStepClick,
}) {
  return (
    <DashboardPageWrapper>
      <button
        type="button"
        onClick={onBackToList}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-5 transition-colors"
      >
        <FiChevronLeft size={16} /> Back to Partner list
      </button>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="grid grid-cols-1 items-start lg:grid-cols-[280px_1fr] lg:items-stretch">
          <div className="bg-white border-b lg:border-b-0 lg:border-r border-gray-100 p-7 w-full">
            <WizardSidebar
              completedStepKeys={completedStepKeys}
              currentStepKey={currentStepKey}
              maxReachedKey={maxReachedKey}
              ifaEmail={ifaEmail}
              percentComplete={percentComplete}
              sidebarHint={sidebarHint}
              onStepClick={onStepClick}
            />
          </div>

          <div className="flex w-full min-h-0 h-auto items-start justify-center p-7 pb-8 lg:min-h-[520px] lg:items-center lg:justify-center lg:p-10">
            <div className="w-full max-w-lg shrink-0">{children}</div>
          </div>
        </div>
      </div>
    </DashboardPageWrapper>
  );
}

// ─── Wizard Left Panel ────────────────────────────────────────────────────────

function WizardSidebar({ completedStepKeys, currentStepKey, maxReachedKey, ifaEmail, percentComplete, sidebarHint, onStepClick }) {
  const maxIdx = WIZARD_STEPS.findIndex(s => s.key === maxReachedKey);

  return (
    <div className="flex flex-col h-auto min-h-0 lg:h-full">
      {/* Header */}
      <div className="mb-6 lg:mb-8">
        <p className="text-xs font-semibold text-green-600 tracking-widest uppercase mb-2">Admin Dashboard</p>
        <h2 className="text-xl font-bold text-gray-900 leading-tight">Partner onboarding</h2>
        {currentStepKey === 'STEP_0_EMAIL' && sidebarHint && (
          <p className="text-sm text-gray-500 mt-1">{sidebarHint}</p>
        )}
        {ifaEmail && (
          <div className="mt-3 px-3 py-2 bg-green-50 rounded-lg border border-green-100">
            <p className="text-xs text-gray-500">Onboarding</p>
            <p className="text-sm font-medium text-gray-800 truncate">{ifaEmail}</p>
          </div>
        )}
      </div>

      {/* Progress bar */}
      {percentComplete > 0 && (
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-1.5">
            <span>Progress</span><span>{percentComplete}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 rounded-full transition-all duration-700" style={{ width: `${percentComplete}%` }} />
          </div>
        </div>
      )}

      {/* Steps list */}
      <div className="flex flex-col gap-1 lg:flex-1">
        {WIZARD_STEPS.map((step, idx) => {
          const isCompleted = completedStepKeys.includes(step.key);
          const isActive = step.key === currentStepKey;
          const isPending = !isCompleted && !isActive;
          // Clickable = any step the session has already reached (idx <= max reached index)
          const isClickable = onStepClick && maxIdx >= 0 && idx <= maxIdx;

          const inner = (
            <>
              {/* Circle indicator */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-all
                  ${isCompleted ? 'bg-green-600 text-white' : ''}
                  ${isActive ? 'bg-green-600 text-white ring-4 ring-green-100' : ''}
                  ${isPending ? 'bg-gray-100 text-gray-400 border-2 border-gray-200' : ''}
                `}
              >
                {isCompleted ? <FiCheck size={14} /> : idx + 1}
              </div>

              {/* Labels */}
              <div className="min-w-0">
                <p className={`text-sm font-semibold leading-tight ${isActive ? 'text-green-700' : isCompleted ? 'text-gray-600' : 'text-gray-400'}`}>
                  {step.label}
                </p>
                <p className={`text-xs mt-0.5 ${isActive ? 'text-green-600' : 'text-gray-400'}`}>
                  {step.sublabel}
                </p>
              </div>

              {isCompleted && (
                <div className="ml-auto">
                  <FiCheck size={14} className="text-green-500" />
                </div>
              )}
            </>
          );

          if (isClickable) {
            return (
              <button
                key={step.key}
                type="button"
                onClick={() => onStepClick(step.key)}
                className={`group flex items-center gap-3 px-3 py-3 rounded-xl transition-all w-full text-left
                  ${isActive ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 hover:border hover:border-gray-200'}
                  ${isCompleted && !isActive ? 'opacity-90' : ''}
                `}
                title={step.label}
              >
                {inner}
              </button>
            );
          }

          return (
            <div
              key={step.key}
              className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all
                ${isActive ? 'bg-green-50 border border-green-200' : ''}
                ${isCompleted ? 'opacity-90' : ''}
              `}
            >
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────


const IFA_NEXT_STEPS = [
  {
    icon: FiLock,
    title: 'Set account password',
    body: 'The partner should open the Partner portal (using the invite link or login flow) and create a secure password for their account before continuing.',
  },
  {
    icon: RiPenNibLine,
    title: 'Sign the onboarding agreement',
    body: 'Agreement e-sign runs in the Partner portal via the DigiSign flow. It is not completed from this admin dashboard.',
  },
  {
    icon: FiBriefcase,
    title: 'Complete the business profile questionnaire',
    body: 'They will answer questions about their business and profile inside the Partner portal. Progress updates when they submit.',
  },
];

function SuccessScreen({ ifaEmail, onViewList, onOnboardAnother }) {
  return (
    <div className="flex flex-col items-center min-h-0 py-8 px-4 sm:py-12 sm:min-h-[50vh] w-full max-w-xl mx-auto">
      <div className="w-full rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="bg-linear-to-br from-green-600 to-green-700 px-6 py-8 sm:px-8 text-center text-white">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 ring-2 ring-white/30">
            <FiCheck size={32} className="text-white" strokeWidth={2.5} />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Admin setup complete</h2>
          <p className="text-green-50/95 text-sm mt-2 max-w-md mx-auto leading-relaxed">
            You&apos;ve finished the steps you can do here. The partner must finish the remaining steps in their own portal.
          </p>
        </div>

        <div className="px-5 py-6 sm:px-8 sm:py-8">
          {ifaEmail && (
            <div className="rounded-xl border border-green-100 bg-green-50/60 px-4 py-3 mb-6 text-left">
              <p className="text-xs font-semibold text-green-800 uppercase tracking-wide">Partner account</p>
              <p className="text-sm font-medium text-gray-900 mt-1 flex items-center gap-2 break-all">
                <FiMail className="text-green-600 shrink-0" size={16} aria-hidden />
                {ifaEmail}
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Share this address with the partner so they know which account to use in the Partner portal.
              </p>
            </div>
          )}

          <div className="text-left mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">What the partner should do next</h3>
            <p className="text-xs text-gray-500 mb-4">
              Ask them to sign in to the <span className="font-medium text-gray-700">Partner portal</span> and complete the following. None of these are done from the admin dashboard.
            </p>
            <ul className="space-y-3">
              {IFA_NEXT_STEPS.map(({ icon: Icon, title, body }) => (
                <li
                  key={title}
                  className="flex gap-3 rounded-xl border border-gray-100 bg-gray-50/80 p-3.5 sm:p-4"
                >
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-green-600">
                    <Icon size={18} aria-hidden />
                  </div>
                  <div className="min-w-0 pt-0.5">
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-amber-100 bg-amber-50/70 px-3.5 py-2.5 text-left">
            <p className="text-xs text-amber-900">
              <span className="font-semibold">Tip for you:</span> You can confirm their progress later from the Partner list or by opening this partner&apos;s onboarding again—status will reflect agreement sign and questionnaire completion.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-xl mt-6">
        <button
          type="button"
          onClick={onOnboardAnother}
          className="flex-1 py-2.5 px-4 rounded-xl font-medium text-sm border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
        >
          Onboard another partner
        </button>
        <button
          type="button"
          onClick={onViewList}
          className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm bg-green-600 text-white hover:bg-green-700 transition-all shadow-md"
        >
          View Partner list
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function IFAOnboardingPage() {
  const { ifaId: paramIfaId } = useParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState(paramIfaId ? 'loading' : 'email'); // 'email' | 'loading' | 'wizard' | 'done'
  const [ifaId, setIfaId] = useState(paramIfaId || null);
  const [ifaEmail, setIfaEmail] = useState('');
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [currentStepKey, setCurrentStepKey] = useState('STEP_1_PAN');
  const [completedStepKeys, setCompletedStepKeys] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [stepRefreshLoading, setStepRefreshLoading] = useState(false);
  // overrideStepKey: set when admin clicks a completed step to go back and edit it
  const [overrideStepKey, setOverrideStepKey] = useState(null);

  const handleGoBack = (stepKey) => {
    // Clicking the real current server-side step always clears the override (returns to current)
    if (stepKey === currentStepKey) {
      setOverrideStepKey(null);
    } else {
      setOverrideStepKey(stepKey);
    }
  };

  const loadOnboardingStatus = useCallback(async (id, opts = {}) => {
    const silent = opts.silent === true;
    if (!silent) setStatusLoading(true);
    try {
      const res = await ifaService.getOnboardingStatus(id);
      const data = res.data;
      setOnboardingStatus(data);
      const wizardStep = mapApiStepToWizard(data.current_step);
      setCurrentStepKey(wizardStep);
      let completedKeys = [
        ...new Set((data.completed_steps?.map((s) => mapCompletedStepKey(s.key || s.step)) || [])),
      ];
      if (wizardStep === 'STEP_COMPLETED') {
        const priorKeys = WIZARD_STEPS.filter((s) => s.key !== 'STEP_COMPLETED').map((s) => s.key);
        completedKeys = [...new Set([...completedKeys, ...priorKeys])];
      }
      setCompletedStepKeys(completedKeys);
      setPhase('wizard');
    } catch (err) {
      toast.error('Failed to load onboarding status.');
      if (!silent) setPhase('email');
    } finally {
      if (!silent) setStatusLoading(false);
    }
  }, []);

  const refreshStatusQuiet = useCallback(async () => {
    if (!ifaId) return;
    setStepRefreshLoading(true);
    try {
      await loadOnboardingStatus(ifaId, { silent: true });
    } finally {
      setStepRefreshLoading(false);
    }
  }, [ifaId, loadOnboardingStatus]);

  // Load status if onboarding id is in the URL
  useEffect(() => {
    if (paramIfaId) {
      setIfaId(paramIfaId);
      loadOnboardingStatus(paramIfaId);
    }
  }, [paramIfaId, loadOnboardingStatus]);

  const handleEmailSuccess = (id, email, isExisting) => {
    setIfaId(id);
    setIfaEmail(email);
    navigate(`/partners/onboard/${id}`, { replace: true });
    loadOnboardingStatus(id);
  };

  const advanceStep = async () => {
    // Reload onboarding status to get the new current_step from the server
    if (ifaId) {
      await loadOnboardingStatus(ifaId);
    }
    setOverrideStepKey(null); // Always clear override after any step saves successfully
  };

  const handleComplete = useCallback(() => {
    setPhase('done');
  }, []);

  const handleOnboardAnother = () => {
    setIfaId(null);
    setIfaEmail('');
    setOnboardingStatus(null);
    setCurrentStepKey('STEP_0_EMAIL');
    setCompletedStepKeys([]);
    setPhase('email');
    navigate('/partners/onboard', { replace: true });
  };

  // ── Render ──

  if (phase === 'email') {
    return (
      <OnboardingWizardLayout
        completedStepKeys={[]}
        currentStepKey="STEP_0_EMAIL"
        ifaEmail=""
        percentComplete={0}
        sidebarHint="Start with the partner's email. Later steps need PAN, bank details, and documents—keep them ready."
        onBackToList={() => navigate('/partners/list')}
      >
        <EmailStep onSuccess={handleEmailSuccess} />
      </OnboardingWizardLayout>
    );
  }

  if (phase === 'loading' || statusLoading) {
    return (
      <DashboardPageWrapper>
        <div className="flex items-center justify-center min-h-0 py-16 sm:min-h-[60vh]">
          <div className="flex flex-col items-center gap-4">
            <svg className="animate-spin h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-sm text-gray-500">Loading onboarding status...</p>
          </div>
        </div>
      </DashboardPageWrapper>
    );
  }

  if (phase === 'done') {
    return (
      <DashboardPageWrapper>
        <SuccessScreen
          ifaEmail={ifaEmail}
          onViewList={() => navigate('/partners/list')}
          onOnboardAnother={handleOnboardAnother}
        />
      </DashboardPageWrapper>
    );
  }

  // Wizard phase
  const pendingDocs = onboardingStatus?.documents?.pending || [];
  const mobile = onboardingStatus?.mobile || '';
  const percentComplete = onboardingStatus?.percentage_complete || 0;
  const displayPercent = currentStepKey === 'STEP_COMPLETED' ? 100 : percentComplete;

  const renderStepForm = () => {
    // Which step form to display: override (going back) or real current
    const activeStep = overrideStepKey ?? currentStepKey;

    switch (activeStep) {
      case 'STEP_1_PAN':
        return <PANStep key={activeStep} ifaId={ifaId} onSuccess={advanceStep} initialPan={onboardingStatus?.pan || ''} />;
      case 'STEP_2_PERSONAL':
      case 'STEP_2_DETAILS':
        return <PersonalStep key={activeStep} ifaId={ifaId} initialMobile={mobile} initialDob={onboardingStatus?.dob || ''} onSuccess={advanceStep} />;
      case 'STEP_3_BANK':
        return (
          <BankStep 
            key={activeStep} 
            ifaId={ifaId} 
            mobile={mobile}
            customerName={onboardingStatus?.name || null}
            onSuccess={advanceStep} 
            prefillBanks={onboardingStatus?.prefill?.banks || []} 
            selectedBankId={onboardingStatus?.prefill?.selected_bank_id} 
          />
        );
      case 'STEP_4_DOCUMENTS':
        return (
          <DocumentsStep
            key={activeStep}
            ifaId={ifaId}
            pendingDocs={pendingDocs}
            uploadedDocs={onboardingStatus?.documents?.uploaded || []}
            onSendInviteComplete={handleComplete}
          />
        );
      case 'STEP_5_AGREEMENT':
      case 'STEP_6_QUESTIONNAIRE':
        return (
          <IfaPortalWaitingPanel
            stepKey={activeStep}
            onRefresh={refreshStatusQuiet}
            refreshLoading={stepRefreshLoading}
          />
        );
      case 'STEP_COMPLETED':
        return (
          <IfaProfileCompletePanel
            ifaEmail={ifaEmail}
            onViewList={() => navigate('/partners/list')}
            onOnboardAnother={handleOnboardAnother}
            onRefresh={refreshStatusQuiet}
            refreshLoading={stepRefreshLoading}
          />
        );
      default:
        return (
          <div className="flex flex-col gap-6 items-center text-center py-6">
            <FiAlertCircle className="w-10 h-10 text-amber-500" />
            <div>
              <p className="text-sm font-medium text-gray-800">Unrecognized step</p>
              <p className="text-xs text-gray-500 mt-1 font-mono">{activeStep}</p>
            </div>
            <GreenButton onClick={refreshStatusQuiet} loading={stepRefreshLoading}>
              Refresh status
            </GreenButton>
          </div>
        );
    }
  };

  return (
    <OnboardingWizardLayout
      completedStepKeys={completedStepKeys}
      currentStepKey={overrideStepKey ?? currentStepKey}
      maxReachedKey={currentStepKey}
      ifaEmail={ifaEmail}
      percentComplete={displayPercent}
      onBackToList={() => navigate('/partners/list')}
      onStepClick={handleGoBack}
    >
      {renderStepForm()}
    </OnboardingWizardLayout>
  );
}
